import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { formatAmount } from "../../utils/utils";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";

// ── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        p: 3,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "10px",
          bgcolor: `${color}22`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>{sub}</Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Simple bar chart (pure CSS/SVG, no lib needed) ─────────────────────────
function BarChart({ data }) {
  const maxVal = Math.max(...data.flatMap((d) => [d.cart, d.booking]), 1);
  const barW = 14;
  const gap = 6;
  const groupW = barW * 2 + gap * 3;
  const chartH = 120;
  const labelH = 20;
  const totalW = data.length * (groupW + 8);

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 1.5, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: "#194049" }} />
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>Purchases</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: "#7c3aed" }} />
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>Bookings</Typography>
        </Box>
      </Box>
      <svg width={totalW} height={chartH + labelH} style={{ display: "block" }}>
        {data.map((d, i) => {
          const x = i * (groupW + 8) + 4;
          const cartH = Math.round((d.cart / maxVal) * chartH);
          const bookH = Math.round((d.booking / maxVal) * chartH);
          return (
            <g key={d.month}>
              {/* cart bar */}
              <rect
                x={x + gap}
                y={chartH - cartH}
                width={barW}
                height={cartH}
                rx={3}
                fill="#194049"
                opacity={0.85}
              />
              {/* booking bar */}
              <rect
                x={x + gap + barW + gap}
                y={chartH - bookH}
                width={barW}
                height={bookH}
                rx={3}
                fill="#7c3aed"
                opacity={0.85}
              />
              {/* label */}
              <text
                x={x + groupW / 2}
                y={chartH + labelH - 2}
                textAnchor="middle"
                fontSize={10}
                fill="#9ca3af"
              >
                {d.month}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

// ── Top 5 list ──────────────────────────────────────────────────────────────
function TopList({ items, valueKey, labelKey, color }) {
  const max = Math.max(...items.map((i) => i[valueKey] || 0), 1);
  return (
    <Box>
      {items.map((item, idx) => (
        <Box key={idx} sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>
              {idx + 1}. {item[labelKey]}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              {item[valueKey]}
            </Typography>
          </Box>
          <Box sx={{ height: 4, bgcolor: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${(item[valueKey] / max) * 100}%`,
                bgcolor: color,
                borderRadius: 2,
                transition: "width 0.4s ease",
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function AdminOverview() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    cartRevenue: 0,
    cartCount: 0,
    bookingRevenue: 0,
    bookingCount: 0,
    soldThisMonth: 0,
    bookingsThisMonth: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [topBookings, setTopBookings] = useState([]);
  const [topItems, setTopItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [txSnap, bookingSnap] = await Promise.all([
          getDocs(collection(db, "transactions")),
          getDocs(collection(db, "booking-list")),
        ]);

        const txDocs = txSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const bookDocs = bookingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Stats
        const paidCart = txDocs.filter((t) => t.type === "cart" && t.status === "paid");
        const paidBookings = bookDocs.filter((b) => b.status === "paid");

        const monthStart = startOfMonth(new Date());
        const monthEnd = endOfMonth(new Date());

        const getDate = (doc) =>
          doc.createdAt?.toDate?.() ||
          (doc.createdAt?.seconds ? new Date(doc.createdAt.seconds * 1000) : null);

        const soldThisMonth = paidCart.filter((t) => {
          const d = getDate(t);
          return d && d >= monthStart && d <= monthEnd;
        }).length;

        const bookingsThisMonth = paidBookings.filter((b) => {
          const d = getDate(b);
          return d && d >= monthStart && d <= monthEnd;
        }).length;

        setStats({
          cartRevenue: paidCart.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0),
          cartCount: paidCart.length,
          bookingRevenue: paidBookings.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0),
          bookingCount: paidBookings.length,
          soldThisMonth,
          bookingsThisMonth,
        });

        // Chart: last 6 months
        const months = Array.from({ length: 6 }, (_, i) => {
          const d = subMonths(new Date(), 5 - i);
          return {
            month: format(d, "MMM"),
            start: startOfMonth(d),
            end: endOfMonth(d),
            cart: 0,
            booking: 0,
          };
        });

        txDocs
          .filter((t) => t.type === "cart" && t.status === "paid")
          .forEach((t) => {
            const date = t.createdAt?.toDate?.() || (t.createdAt?.seconds ? new Date(t.createdAt.seconds * 1000) : null);
            if (!date) return;
            const m = months.find((m) => date >= m.start && date <= m.end);
            if (m) m.cart += parseFloat(t.amount) || 0;
          });

        bookDocs
          .filter((b) => b.status === "paid")
          .forEach((b) => {
            const date = b.createdAt?.toDate?.() || (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : null);
            if (!date) return;
            const m = months.find((m) => date >= m.start && date <= m.end);
            if (m) m.booking += parseFloat(b.amount) || 0;
          });

        setChartData(months);

        // Top 5 bookings by service
        const serviceMap = {};
        paidBookings.forEach((b) => {
          const key = b.service || "Unknown";
          serviceMap[key] = (serviceMap[key] || 0) + 1;
        });
        const sorted = Object.entries(serviceMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, count]) => ({ name, count }));
        setTopBookings(sorted);

        // Top 5 items (aggregate from transaction goods arrays)
        const itemMap = {};
        txDocs
          .filter((t) => t.status === "paid" && Array.isArray(t.goods))
          .forEach((t) => {
            t.goods.forEach((g) => {
              const key = g.name || g.title || "Unknown";
              itemMap[key] = (itemMap[key] || 0) + (g.quantity || 1);
            });
          });
        const sortedItems = Object.entries(itemMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, qty]) => ({ name, qty }));
        setTopItems(sortedItems);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
        <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
          Admin overview — all activity across the store
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {[
            { to: "/", label: "Home", icon: <HomeOutlinedIcon sx={{ fontSize: 15 }} /> },
            { to: "/shop", label: "Shop", icon: <StorefrontOutlinedIcon sx={{ fontSize: 15 }} /> },
          ].map(({ to, label, icon }) => (
            <Box
              key={to}
              component={Link}
              to={to}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.75,
                py: 0.75,
                bgcolor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                textDecoration: "none",
                fontSize: 13,
                color: "#374151",
                fontWeight: 500,
                transition: "border-color 0.15s, color 0.15s",
                "&:hover": { borderColor: "#194049", color: "#194049" },
              }}
            >
              {icon}
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { icon: <ShoppingBagIcon />, label: "Cart Revenue", value: formatAmount(stats.cartRevenue), sub: `${stats.cartCount} orders`, color: "#194049" },
          { icon: <CalendarMonthIcon />, label: "Booking Revenue", value: formatAmount(stats.bookingRevenue), sub: `${stats.bookingCount} bookings`, color: "#7c3aed" },
          { icon: <PendingActionsIcon />, label: "Products Sold This Month", value: stats.soldThisMonth, sub: "paid orders", color: "#f59e0b" },
          { icon: <TrendingUpIcon />, label: "Bookings This Month", value: stats.bookingsThisMonth, sub: "paid bookings", color: "#ef4444" },
        ].map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Chart */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", p: 3, mb: 3 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 2 }}>
          Purchases vs Bookings — Last 6 Months
        </Typography>
        <BarChart data={chartData} />
      </Box>

      {/* Top 5 */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", p: 3 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 2 }}>
              Top Booked Services
            </Typography>
            {topBookings.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>No paid bookings yet</Typography>
            ) : (
              <TopList items={topBookings} labelKey="name" valueKey="count" color="#7c3aed" />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", p: 3 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 2 }}>
              Top Selling Items
            </Typography>
            {topItems.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>No sales data yet</Typography>
            ) : (
              <TopList items={topItems} labelKey="name" valueKey="qty" color="#194049" />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
